/**
 * RelatedProductsCarousel — Horizontal scrolling carousel of related products
 * (spare parts for DTH hammers) on the PDP page.
 *
 * Props:
 *   products      — array of product objects (related spare parts)
 *   userState     — 'guest' | 'employee-viewer' | 'customer' | 'employee-admin'
 *   onViewDetails — function(itemCode) for navigation to product PDP
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { isCommerceEnabledForProductLine, getProductDisplayName } from '@data/index';

export default function RelatedProductsCarousel({ products, userState, onViewDetails }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [quantities, setQuantities] = useState({});

  const isCustomerLike = userState === 'customer' || userState === 'employee-admin';

  // Initialize default quantities
  useEffect(() => {
    if (!products?.length) return;
    const defaults = {};
    products.forEach((p) => {
      if (!(p.itemCode in (quantities || {}))) {
        defaults[p.itemCode] = 1;
      }
    });
    if (Object.keys(defaults).length > 0) {
      setQuantities((prev) => ({ ...defaults, ...prev }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  // Track scroll position for arrow visibility
  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, products]);

  if (!products?.length) return null;

  const scrollBy = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = direction === 'left' ? -280 : 280;
    el.scrollBy({ left: amount, behavior: 'smooth' });
  };

  const updateQuantity = (itemCode, delta) => {
    setQuantities((prev) => ({
      ...prev,
      [itemCode]: Math.max(1, (prev[itemCode] || 1) + delta),
    }));
  };

  const setQuantityDirect = (itemCode, value) => {
    const val = parseInt(value, 10);
    if (!isNaN(val) && val >= 1) {
      setQuantities((prev) => ({ ...prev, [itemCode]: val }));
    }
  };

  return (
    <div className="relative">
      {/* Webkit scrollbar hide */}
      <style>{`.scrollbar-hide::-webkit-scrollbar { display: none; }`}</style>

      {/* Left arrow (desktop only) */}
      {canScrollLeft && (
        <button
          type="button"
          onClick={() => scrollBy('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full w-10 h-10 items-center justify-center hover:bg-n1 transition-colors hidden md:flex"
          aria-label="Scroll left"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      {/* Right arrow (desktop only) */}
      {canScrollRight && (
        <button
          type="button"
          onClick={() => scrollBy('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full w-10 h-10 items-center justify-center hover:bg-n1 transition-colors hidden md:flex"
          aria-label="Scroll right"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      {/* Scrollable product cards */}
      <div
        ref={scrollRef}
        className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-4 px-4 scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {products.map((product) => (
          <div
            key={product.itemCode}
            className="min-w-[260px] max-w-[280px] snap-start flex-shrink-0 border border-n2 p-4 bg-white hover:shadow-md transition-shadow"
          >
            {/* Product image */}
            <div className="w-full h-32 bg-n1 rounded flex items-center justify-center mb-3">
              {(product.images?.[0]?.src || product.imageUrl) ? (
                <img
                  src={product.images?.[0]?.src || product.imageUrl}
                  alt={getProductDisplayName(product) || product.itemCode}
                  className="max-h-full max-w-full object-contain p-2"
                  loading="lazy"
                />
              ) : (
                <span className="text-n5 text-sm">No image</span>
              )}
            </div>

            {/* Display name — falls back through descriptionShort → name →
                description so the card populates for every product type
                (grinding cups only carry `name`/`descriptionShort`, TH/DTH
                carry `description`, rotary bits carry `descriptionShort`). */}
            <p className="font-sandvik-text text-carbon-black text-sm line-clamp-2 mb-1">
              {getProductDisplayName(product) || product.itemCode}
            </p>

            {/* Part number in small caption — matches the PLP card pattern */}
            <p className="text-xs text-n5 font-sandvik-text mb-3">
              {product.itemCode}
            </p>

            {/* Quantity selector (customer / employee-admin only, commerce-enabled lines only) */}
            {isCustomerLike && isCommerceEnabledForProductLine(product.productLine) && (
              <div className="flex items-center border border-n3 rounded overflow-hidden mb-2">
                <button
                  type="button"
                  className="bg-n1 px-3 py-2 text-n6 hover:bg-n2 font-normal"
                  onClick={() => updateQuantity(product.itemCode, -1)}
                  aria-label={`Decrease quantity for ${product.description}`}
                >
                  &minus;
                </button>
                <input
                  type="number"
                  className="w-16 text-center border-x border-n3 py-2 font-sandvik-text"
                  value={quantities[product.itemCode] || 1}
                  min={1}
                  onChange={(e) => setQuantityDirect(product.itemCode, e.target.value)}
                  aria-label={`Quantity for ${product.description}`}
                />
                <button
                  type="button"
                  className="bg-n1 px-3 py-2 text-n6 hover:bg-n2 font-normal"
                  onClick={() => updateQuantity(product.itemCode, 1)}
                  aria-label={`Increase quantity for ${product.description}`}
                >
                  +
                </button>
              </div>
            )}

            {/* Add to cart button (gated by userState AND product-line commerce) */}
            {isCommerceEnabledForProductLine(product.productLine) && (
              isCustomerLike ? (
                <button
                  type="button"
                  className="w-full bg-sandvik-blue text-white py-2 rounded text-sm font-sandvik-text font-medium hover:bg-sandvik-blue/90 transition-colors mb-2"
                >
                  Add to cart
                </button>
              ) : (
                <button
                  type="button"
                  className="w-full bg-n2 text-n5 py-2 rounded text-sm font-sandvik-text cursor-not-allowed mb-2"
                  disabled
                >
                  Add to cart
                </button>
              )
            )}

            {/* View details link */}
            <button
              type="button"
              onClick={() => onViewDetails(product.itemCode)}
              className="text-electric-blue text-sm font-sandvik-text hover:underline"
            >
              View details &rsaquo;
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
